import { ResourceLoader, Workflow, useScopedSelector } from 'ia-react-core';
import React, { FC, useCallback } from 'react';
import { FormLayoutProps } from 'omni-package-react-core';
import getMfaPasswordStep from './steps/password/getMfaPasswordStep';
import getMfaInformationsStep from './steps/informations/getMfaInformationsStep';
import getMfaEmailStep from './steps/email/getMfaEmailStep';
import getMfaEmailCodeStep from './steps/emailCode/getMfaEmailCodeStep';
import getMfaPhoneStep from './steps/phone/getMfaPhoneStep';
import getMfaPhoneCodeStep from './steps/phoneCode/getMfaPhoneCodeStep';
import getMfaConfirmationStep from './steps/confirmation/getMfaConfirmationStep';
import { getIdentityManagementApiRoot } from '../../utils/getIdentityManagementApiRoot';
import { MfaValidationPageState } from '../../interfaces/MfaValidationPageState';
import { MfaInformations } from '../../interfaces/MfaInformations';
import { MfaFactors } from '../../interfaces/MfaFactors';
import { MfaOnboardingResourceInfo } from '../../components/MfaOnboardingResource';
import { MfaOnboarding } from '../../interfaces/MfaOnboarding';

export interface MfaValidationPageProps {
  FormLayout: FC<FormLayoutProps>;
  returnPage: string;
}

const MfaValidationPage: FC<MfaValidationPageProps> = ({ returnPage, FormLayout }) => {
  const isConfirmed = useScopedSelector((state: MfaValidationPageState) => state?.values?.mfa?.confirmation?.isConfirmed);
  const hasConfirmedPassword = useScopedSelector((state: MfaValidationPageState) => state?.values?.mfa?.password?.hasConfirmedPassword);
  const mfaOnboarding: MfaOnboarding = useScopedSelector(MfaOnboardingResourceInfo.dataDst);
  const isCompleted = mfaOnboarding?.data?.onboardingStatus === 'completed';

  const transformResponse = useCallback((response: MfaFactors): MfaInformations => {
    const baseInfos = { email: '', phone: '' };

    if (!response?.data?.factorDetails?.length) return baseInfos;

    const infos = response.data?.factorDetails.reduce<MfaInformations>((result, details) => {
      if (details.type === 'sms') {
        result.phone = details.information;
      } else if (details.type === 'email') {
        result.email = details.information;
      }
      return result;
    }, baseInfos);

    return infos;
  }, []);

  return (
    <FormLayout
      canQuit={Boolean(isConfirmed || (isCompleted && !hasConfirmedPassword))}
      needHelp
      returnPage={isCompleted ? returnPage : '/'}
      name="mfa-validation"
      lob="corpo"
    >
      <ResourceLoader<MfaFactors>
        name="resources.informations"
        url={`${getIdentityManagementApiRoot()}/factors`}
        dataDst="data.informations"
        transformResponse={transformResponse}
      >
        <Workflow
          name="workflows.mfa"
          valuesDst="values.mfa"
        >
          {getMfaPasswordStep()}
          {getMfaInformationsStep()}
          {getMfaEmailStep()}
          {getMfaEmailCodeStep()}
          {getMfaPhoneStep()}
          {getMfaPhoneCodeStep()}
          {getMfaConfirmationStep()}
        </Workflow>
      </ResourceLoader>
    </FormLayout>
  );
};

export default MfaValidationPage;
